import axios from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { BASE_URL_SERVER } from '../../../ini.json'
import {
  BusinessContact,
  BusinessContactPagination,
  BusinessContactQueryParams,
  BusinessContactResponse,
} from '../../common/businessContactTypes'
import { useAppSelector } from '../../redux/hooks'

export type BusinessContactTableItem = BusinessContact

export interface UseBusinessContactDataResult {
  data: BusinessContactTableItem[]
  loading: boolean
  error: boolean
  pagination: BusinessContactPagination
  params: BusinessContactQueryParams
  setParams: (params: Partial<BusinessContactQueryParams>) => void
  refetch: () => void
}

const DEFAULT_PAGINATION: BusinessContactPagination = {
  page: 1,
  pageSize: 50,
  total: 0,
  totalPages: 0,
}

export const useBusinessContactData = (
  initialParams: BusinessContactQueryParams = {}
): UseBusinessContactDataResult => {
  const token = useAppSelector(state => state.myProfile.token)

  const [data, setData] = useState<BusinessContactTableItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [pagination, setPagination] =
    useState<BusinessContactPagination>(DEFAULT_PAGINATION)
  const [params, setParamsState] = useState<BusinessContactQueryParams>({
    page: 1,
    pageSize: 50,
    ...initialParams,
  })

  const queryString = useMemo(() => {
    const searchParams = new URLSearchParams()

    if (params.page) searchParams.set('page', params.page.toString())
    if (params.pageSize)
      searchParams.set('pageSize', params.pageSize.toString())
    if (params.orderBy) searchParams.set('orderBy', params.orderBy)
    if (params.search) searchParams.set('search', params.search)
    if (params.state) searchParams.set('state', params.state)
    if (params.locality) searchParams.set('locality', params.locality)
    if (params.postalCode) searchParams.set('postalCode', params.postalCode)

    return searchParams.toString()
  }, [params])

  const fetchData = useCallback(async () => {
    if (!token) return

    setLoading(true)
    setError(false)

    try {
      const url = `${BASE_URL_SERVER}/api/business-contact/data/${token}${
        queryString ? `?${queryString}` : ''
      }`
      const response = await axios.get<BusinessContactResponse>(url)

      setData(response.data.data)
      setPagination(response.data.pagination)
    } catch (err) {
      console.error('BusinessContact data error:', err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [token, queryString])

  const setParams = useCallback(
    (newParams: Partial<BusinessContactQueryParams>) => {
      setParamsState(prev => ({
        ...prev,
        ...newParams,
        // Reset to page 1 when filters change (but not when page itself changes)
        page:
          newParams.page !== undefined
            ? newParams.page
            : newParams.search !== undefined ||
              newParams.state !== undefined ||
              newParams.locality !== undefined ||
              newParams.postalCode !== undefined
            ? 1
            : prev.page,
      }))
    },
    []
  )

  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, pagination, params, setParams, refetch }
}
